import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import UsersPage from "./pages/UsersPage";
import TournamentsPage from "./pages/TournamentsPage";
import AgendaPage from "./pages/OrgTournamentsPage";
import OrgStatsPage from "./pages/OrgStatsPage";
import IframeManagerPage from "./pages/IframeManagerPage";
import RolesPage from "./pages/RolesPage";
import OrgProfilePage from "./pages/OrgProfilePage";
import EmbedPage from "./pages/EmbedPage";
import ActivityLogsPage from "./pages/admin/ActivityLogsPage";
import UserDetailPage from "./pages/admin/UserDetailPage";
import NotificationsPage from "./pages/admin/NotificationsPage";
import CommissionSettingsPage from "./pages/admin/CommissionSettingsPage";
import ShopsPage from "./pages/admin/ShopsPage";
import ShopRequestsPage from "./pages/admin/ShopRequestsPage";
import MaintenancePage from "./pages/MaintenancePage";
import DashboardProPage from "./pages/DashboardProPage";
import PaymentSettingsPage from "./pages/PaymentSettingsPage";
import TournamentManager from "./pages/TournamentManager";
import CreateTournamentPage from "./pages/CreateTournamentPage";
import { PublicEventsPage, PublicTournamentDetail } from "./pages/PublicEventsPage";
import DeepLinkRedirect from "./pages/DeepLinkRedirect";
import ShareableEventPage from "./pages/ShareableEventPage";
import PlayerDiscoverPage from "./pages/PlayerDiscoverPage";
import PlayerMyEventsPage from "./pages/PlayerMyEventsPage";
import PlayerProfilePage from "./pages/PlayerProfilePage";
import PlayerEventPage from "./pages/PlayerEventPage";
import PublicUserProfilePage from "./pages/PublicUserProfilePage";
import CreateShopPage from "./pages/CreateShopPage";
import AppMobilePage from "./pages/AppMobilePage";
import ShopProfilePage from "./pages/ShopProfilePage";
import ShopStatsPage from "./pages/ShopStatsPage";
import AllTournamentsPage from "./pages/AllTournamentsPage";
import CreatePlayerEventPage from "./pages/CreatePlayerEventPage";
import PlayerGamePage from "./pages/PlayerGamePage";
import MessagesPage from "./pages/MessagesPage";
import PaymentReturnPage from "./pages/PaymentReturnPage";

import LandingPage from "./pages/LandingPage";
import BackOfficePage from "./pages/admin/BackOfficePage";
import ShopLayout from "./components/ShopLayout";
import ShopTournamentsPage from "./pages/ShopTournamentsPage";
import {
  Trophy, Users, LayoutDashboard, LogOut,
  ChevronLeft, ChevronRight, Code2, Shield, BarChart3,
  UserCog, Plus, CalendarDays,
  Bell, History, Megaphone, CreditCard, Percent, Settings, Globe, Store,
} from "lucide-react";

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, canAccessAdmin } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]"></div></div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!canAccessAdmin) return <Navigate to="/home" replace />;
  return children;
};

const OrgRoute = ({ children }) => {
  const { isAuthenticated, loading, canAccessAdmin } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]"></div></div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!canAccessAdmin) return <Navigate to="/home" replace />;
  return children;
};

const PlayerRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]"></div></div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

const Sidebar = ({ collapsed, setCollapsed, onNavigate }) => {
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();

  const orgItems = [
    { path: "/shop/tournaments", label: "Mes Tournois", icon: Trophy },
    { path: "/create-tournament", label: "Creer un tournoi", icon: Plus },
  ];

  const adminItems = [
    { type: "separator", label: "Administration" },
    { path: "/admin/users", label: "Utilisateurs", icon: Users },
    { path: "/admin/tournaments", label: "Tous les tournois", icon: Trophy },
    { path: "/admin/notifications", label: "Notifications", icon: Megaphone },
    { path: "/admin/commissions", label: "Commissions", icon: Percent },
    { path: "/admin/activity-logs", label: "Journal", icon: History },
    { path: "/admin/backoffice", label: "Back Office", icon: Settings },
    { path: "/admin/shops", label: "Boutiques", icon: Store },
  ];

  const navItems = isAdmin ? [...orgItems, ...adminItems] : orgItems;
  const isActive = (path) => location.pathname === path;

  return (
    <aside className={`h-full bg-[#121212] border-r border-[#333] transition-all duration-300 ${collapsed ? 'w-16' : 'w-64'}`}>
      <div className="h-16 flex items-center justify-between px-4 border-b border-[#333]">
        {!collapsed && (
          <Link to="/shop/tournaments" className="flex items-center gap-2">
            <img src="/logo-complet-arcanapp.png" alt="Arcanapp" className="h-8" />
          </Link>
        )}
        {collapsed && (
          <Link to="/shop/tournaments" className="flex items-center justify-center">
            <img src="/logo-new.png" alt="Arcanapp" className="w-8 h-8 rounded-md" />
          </Link>
        )}
        <button onClick={() => setCollapsed(!collapsed)} className="p-2 rounded-md hover:bg-[#252525] text-[#666] hover:text-white transition-colors">{collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}</button>
      </div>

      <nav className="p-2 space-y-1 overflow-y-auto" style={{ height: 'calc(100% - 130px)' }}>
        {navItems.map((item, i) => {
          if (item.type === "separator") {
            return !collapsed ? <div key={i} className="pt-4 pb-1 px-3"><span className="text-xs font-semibold text-[#666] uppercase tracking-wider">{item.label}</span></div> : <div key={i} className="my-2 mx-2 border-t border-[#333]" />;
          }
          return (
            <Link key={item.path} to={item.path} onClick={onNavigate} className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${isActive(item.path) ? 'bg-[#FF6B35]/10 text-[#FF6B35]' : 'text-[#B0B0B0] hover:bg-[#252525] hover:text-white'}`} data-testid={`nav-${item.path.slice(1).replace('/', '-')}`}>
              <div className="relative flex-shrink-0">
                <item.icon className="w-5 h-5" />
                {item.badge > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1" data-testid="unread-badge">{item.badge > 99 ? '99+' : item.badge}</span>}
              </div>
              {!collapsed && <span className="font-medium text-sm flex-1">{item.label}</span>}
              {!collapsed && item.badge > 0 && <span className="bg-red-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[20px] text-center">{item.badge > 99 ? '99+' : item.badge}</span>}
            </Link>
          );
        })}
      </nav>

      <div className="absolute bottom-0 left-0 right-0 p-2 border-t border-[#333]">
        <button onClick={logout} className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-[#B0B0B0] hover:bg-red-500/10 hover:text-red-400 transition-colors" data-testid="logout-button"><LogOut className="w-5 h-5 flex-shrink-0" />{!collapsed && <span className="font-medium text-sm">Déconnexion</span>}</button>
      </div>
    </aside>
  );
};

const AdminLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  return (
    <div className="h-screen overflow-hidden bg-[#0a0a0a]">
      {/* Mobile overlay */}
      {mobileOpen && <div className="fixed inset-0 z-40 bg-black/60 md:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Mobile hamburger */}
      {isMobile && !mobileOpen && (
        <button onClick={() => setMobileOpen(true)} className="fixed top-4 left-4 z-30 p-2.5 rounded-xl bg-[#1a1a1a] border border-[#333] text-white shadow-lg" data-testid="mobile-menu-btn">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
        </button>
      )}

      {/* Sidebar */}
      <div className={`fixed left-0 top-0 h-screen z-50 transition-transform duration-300 ${isMobile ? (mobileOpen ? 'translate-x-0' : '-translate-x-full') : 'translate-x-0'}`}>
        <Sidebar collapsed={isMobile ? false : collapsed} setCollapsed={setCollapsed} onNavigate={() => { if (isMobile) setMobileOpen(false); }} />
      </div>

      <main className={`h-screen overflow-y-auto transition-all duration-300 ${isMobile ? 'ml-0' : (collapsed ? 'ml-16' : 'ml-64')}`}>
        <div className={`p-4 md:p-8 ${isMobile ? 'pt-16' : ''}`}>{children}</div>
      </main>
    </div>
  );
};

const AppRoutes = () => {
  const { isAuthenticated, loading, canAccessAdmin, isOrg, hasShop, selectedShop } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]"></div></div>;

  // Mode maintenance : les visiteurs non connectés voient la page de reconstruction.
  // /login reste accessible (accès pro/admin) ; /embed reste actif (widgets des boutiques).
  const isShopConnected = isOrg || hasShop || !!selectedShop;
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/embed/:orgId" element={<EmbedPage />} />
        {/* Public shareable pages (Facebook-style shop pages, events, profiles) */}
        <Route path="/e/:type/:id" element={<ShareableEventPage />} />
        <Route path="/player/event/:type/:id" element={<ShopLayout><PlayerEventPage /></ShopLayout>} />
        <Route path="/user/:id" element={<ShopLayout><PublicUserProfilePage /></ShopLayout>} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/all-tournaments" element={<ShopLayout><AllTournamentsPage /></ShopLayout>} />
        <Route path="/:slug" element={<ShopProfilePage />} />
        <Route path="*" element={<MaintenancePage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Public routes - no auth needed */}
      <Route path="/embed/:orgId" element={<EmbedPage />} />
      <Route path="/events" element={<PublicEventsPage />} />
      <Route path="/events/tournament/:id" element={<PublicTournamentDetail />} />
      <Route path="/open/:type/:id" element={<DeepLinkRedirect />} />
      <Route path="/e/:type/:id" element={<ShareableEventPage />} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/login" element={isAuthenticated ? <Navigate to="/home" replace /> : <LoginPage />} />

      {/* Landing page */}
      <Route path="/" element={isAuthenticated ? <Navigate to="/home" replace /> : <LandingPage />} />

      {/* Home - discover tournaments */}
      <Route path="/home" element={<ShopLayout>{isShopConnected ? <DashboardProPage /> : <PlayerDiscoverPage />}</ShopLayout>} />
      <Route path="/all-tournaments" element={<ShopLayout><AllTournamentsPage /></ShopLayout>} />
      <Route path="/player/create-game" element={<ShopLayout><CreatePlayerEventPage /></ShopLayout>} />
      <Route path="/game/:id" element={<ShopLayout><PlayerGamePage /></ShopLayout>} />
      <Route path="/discover" element={<Navigate to="/home" replace />} />
      <Route path="/player/discover" element={<Navigate to="/home" replace />} />
      <Route path="/player/event/:type/:id" element={<ShopLayout><PlayerEventPage /></ShopLayout>} />
      <Route path="/player/user/:id" element={<ShopLayout><PublicUserProfilePage /></ShopLayout>} />
      <Route path="/user/:id" element={<ShopLayout><PublicUserProfilePage /></ShopLayout>} />

      {/* Authenticated routes */}
      <Route path="/my-tournaments" element={<PlayerRoute><ShopLayout><PlayerMyEventsPage /></ShopLayout></PlayerRoute>} />
      <Route path="/player/my-events" element={<Navigate to="/my-tournaments" replace />} />
      <Route path="/profile" element={<PlayerRoute><ShopLayout><PlayerProfilePage /></ShopLayout></PlayerRoute>} />
      <Route path="/messages" element={<PlayerRoute><ShopLayout><MessagesPage /></ShopLayout></PlayerRoute>} />
      <Route path="/payment/success" element={<PaymentReturnPage />} />
      <Route path="/payment/cancel" element={<PaymentReturnPage cancelled />} />
      <Route path="/player/profile" element={<Navigate to="/profile" replace />} />
      <Route path="/player/create-shop" element={<PlayerRoute><ShopLayout><CreateShopPage /></ShopLayout></PlayerRoute>} />
      <Route path="/my-shop" element={<OrgRoute><ShopLayout><CreateShopPage editMode /></ShopLayout></OrgRoute>} />
      <Route path="/shop/stats" element={<OrgRoute><ShopLayout><ShopStatsPage /></ShopLayout></OrgRoute>} />
      <Route path="/app-mobile" element={<ShopLayout><AppMobilePage /></ShopLayout>} />

      {/* Shop/ORG routes */}
      <Route path="/shop/tournaments" element={<PlayerRoute><ShopLayout><ShopTournamentsPage /></ShopLayout></PlayerRoute>} />
      <Route path="/dashboard" element={<Navigate to="/shop/tournaments" replace />} />
      <Route path="/agenda" element={<Navigate to="/shop/tournaments" replace />} />
      <Route path="/create-tournament" element={<OrgRoute><ShopLayout><CreateTournamentPage /></ShopLayout></OrgRoute>} />
      <Route path="/stats" element={<PlayerRoute><ShopLayout><OrgStatsPage /></ShopLayout></PlayerRoute>} />
      <Route path="/iframe" element={<OrgRoute><ShopLayout><IframeManagerPage /></ShopLayout></OrgRoute>} />
      <Route path="/payment" element={<OrgRoute><ShopLayout><PaymentSettingsPage /></ShopLayout></OrgRoute>} />
      <Route path="/roles" element={<OrgRoute><ShopLayout><RolesPage /></ShopLayout></OrgRoute>} />
      <Route path="/tournament/:id/manage" element={<PlayerRoute><ShopLayout><TournamentManager /></ShopLayout></PlayerRoute>} />

      {/* Admin routes */}
      {isAuthenticated && (
        <>
          <Route path="/admin/users" element={<ProtectedRoute><ShopLayout><UsersPage /></ShopLayout></ProtectedRoute>} />
          <Route path="/admin/users/:userId" element={<ProtectedRoute><ShopLayout><UserDetailPage /></ShopLayout></ProtectedRoute>} />
          <Route path="/admin/tournaments" element={<ProtectedRoute><ShopLayout><TournamentsPage /></ShopLayout></ProtectedRoute>} />
          <Route path="/admin/notifications" element={<ProtectedRoute><ShopLayout><NotificationsPage /></ShopLayout></ProtectedRoute>} />
          <Route path="/admin/commissions" element={<ProtectedRoute><ShopLayout><CommissionSettingsPage /></ShopLayout></ProtectedRoute>} />
          <Route path="/admin/activity-logs" element={<ProtectedRoute><ShopLayout><ActivityLogsPage /></ShopLayout></ProtectedRoute>} />
          <Route path="/admin/backoffice" element={<ProtectedRoute><ShopLayout><BackOfficePage /></ShopLayout></ProtectedRoute>} />
          <Route path="/admin/shops" element={<ProtectedRoute><ShopLayout><ShopsPage /></ShopLayout></ProtectedRoute>} />
          <Route path="/admin/shop-requests" element={<ProtectedRoute><ShopLayout><ShopRequestsPage /></ShopLayout></ProtectedRoute>} />
        </>
      )}

      <Route path="/:slug" element={<ShopLayout><ShopProfilePage /></ShopLayout>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '1028472079115-97u44ggvsts0msnu7fjiv10kj01maorg.apps.googleusercontent.com';

import CookieConsent from "./components/CookieConsent";

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <CookieConsent />
          <Toaster position="top-right" toastOptions={{ style: { background: '#1a1a1a', color: '#fff', border: '1px solid #333' } }} />
        </BrowserRouter>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
